import { AnyError } from "../../errorHandling/anyError.ts";
import { AsyncFunction } from "../../types/functions.ts";
import { useCallback, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

export interface AsyncState<Result> {
	loading?: boolean;
	data?: Result;
	error?: AnyError;
}

export type AsyncDataAsyncSetter<Result> = (fetchData: () => Promise<Result>) => Promise<Result>;

export type AsyncDataImmediateSetter<Result> = (setter: (data: Result) => Result) => void;

export const useAsyncState = <Result>(
	defaultValue?: Result,
): readonly [
	AsyncState<Result>,
	AsyncDataAsyncSetter<Result>,
	AsyncDataImmediateSetter<Result>,
] => {
	const [asyncState, setAsyncState] = useState<AsyncState<Result>>({
		data: defaultValue,
	});

	const callId = useRef<string>("initial");

	const asyncSetter = useCallback(
		async (fetch: AsyncFunction<void, Result>): Promise<Result> => {
			const currentCallId = uuidv4();
			callId.current = currentCallId;
			try {
				if (currentCallId === callId.current) {
					setAsyncState((prev) => {
						return {
							data: prev.data,
							loading: true,
						};
					});
				}
				const actionResult = await fetch();
				if (currentCallId === callId.current) {
					setAsyncState({
						loading: false,
						data: actionResult,
					});
				}
				return actionResult;
			} catch (error) {
				if (error instanceof Error) {
					if (currentCallId === callId.current) {
						setAsyncState({ error: error });
					}
					throw error;
				} else {
					const unknownError = Error(
						"Unknown error in API client, check console for more info",
					);
					if (currentCallId === callId.current) {
						setAsyncState({ error: unknownError });
					}
					throw unknownError;
				}
			}
		},
		[setAsyncState, callId],
	);

	const immediateSetter = useCallback(
		(setter: (data: Result) => Result) => {
			setAsyncState((prev) => mapAsyncState(prev, setter));
		},
		[setAsyncState],
	);

	return [asyncState, asyncSetter, immediateSetter] as const;
};

export const mapAsyncState = <T, U>(state: AsyncState<T>, mapper: (data: T) => U) => {
	if (state.data !== undefined)
		return {
			...state,
			data: mapper(state.data),
		};

	return {
		...state,
		data: undefined,
	};
};
